/**
 * Home page.
 *
 * @module app/pages/HomePage
 */
import _ from 'lodash';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import DeviceDetector from '../components/DeviceDetector';
import FirstTimeUserJourney from '../components/FirstTimeUserJourney';
import LazyLoadingImage from '../components/LazyLoadingImage';
import LiveScore from '../components/LiveScore';
import MarketMenuAdapter from '../components/MarketMenuAdapter';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import SwiperNavigation from '../components/SwiperNavigation';
import Tabs from '../components/Tabs';
import URLProvider from '../components/URLProvider';
import HiddenMarketsWidget from '../components/HiddenMarketsWidget';
import EnhancedOddsDiffusionAdapter from '../components/push/EnhancedOddsDiffusionAdapter';
import EventViewAdapter from '../views/EventViewAdapter';
import { renderNewBanners } from '../components/newBanners/newBanners';
import { renderEpicValueCarousel, HOMEPAGE_KEY } from '../components/epicValueCarousel';
import { renderGamingWidget } from '../components/gamingWidget';
import { tryRenderStories, STORYLY_SUPPORTED_PAGES } from '../components/stories';

/**
 * Internal state flag used to determine whether page has been rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Content of home section
 *
 * @type {Object}
 */
var homeContent = null;

/**
 * Window object
 *
 * @type {Object}
 */
var $window = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Events for lazy loading
 *
 * @type {String}
 */
var events = 'scroll.home-lazy-load, resize.home-lazy-load';

/**
 * Map tab names with url parts
 *
 * @type {Object}
 */
var tabMap = {
    'Enhanced': 'enhanced',
    'Next Off': 'nextOff',
    'In Play': 'inPlay',
    'Highlights': 'highlights',
    'Top Bets': 'topBets',
};

/**
 * List of home panels to be hidden in desktop devices
 *
 * @type {Array}
 */
var hiddenPanelsInDesktop = [
    'Top Bets',
];

/**
 * Loading counter
 *
 * @type {Number}
 */
var loadingCounter = 0;

/**
 * Loaded tabs collection
 *
 * @type {Array}
 */
var loadedTabs = [];

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * Subscribing model
 *
 * @param   {Object}  model  The model object
 *
 * @returns {void}
 *
 * @private
 */
var subscribeModel = function subscribeModel(model) {

    SelectionViewAdapter.init(model.selections, content);
    _.each(model, subscribeSetToPush);
    _.each(
        model.events,
        (anEvent) => LiveScore.subscribe(anEvent, content)
    );
    _.each(
        model.events,
        (anEvent) => {
            if (anEvent.hasEnhancedOdds){
                EnhancedOddsDiffusionAdapter
                    .subscribe(anEvent.id, EventViewAdapter.wasPrice(anEvent.id, content).update);
            }
        }
    );
};

/**
 * Checking if event already exists in model
 *
 * @param   {Object} event The event object
 *
 * @returns {Boolean}
 *
 * @private
 */
var existsInModel = function existInModel({ id }) {

    return !window.WH.sportsbook.model.events.hasOwnProperty(id);
};

/**
 * Function used to initialize views on the page
 *
 * @returns {void}
 *
 * @private
 */
var initViews = function initViews() {

    BasePage.setOddsFormat();
    subscribeModel(deps.sportsDataRepository.getModel());
    LazyLoadingImage.init();

    renderDates();
    manageOfferClubLink();
    renderEpicValueCarousel(HOMEPAGE_KEY);
    renderGamingWidget();
    tryRenderStories(STORYLY_SUPPORTED_PAGES.HOMEPAGE);


    if(window.WH.areNewBannersEnabled) {
        renderNewBanners('homepage');
    }
};

/**
 * Adds the non-route class to the offer club link, if on the native app, appends a querystring param to remove the
 * header and footer on the offer club page
 *
 * @return {void}
 *
 * @private
 */
var manageOfferClubLink = function manageOfferClubLink() {

    var offerClubLink = BasePage.jQuery('#carousel i.offer-club-sprite').parents('a');
    offerClubLink.addClass('non-route');

    if (offerClubLink && offerClubLink.length > 0 && DeviceDetector.isNative()) {
        offerClubLink.attr('href', offerClubLink.attr('href').replace(/\/?$/, '/?showHeaderFooter=false'));
    }
};

/**
 * Render all dates for current scope
 *
 * @returns {void}
 *
 * @private
 */
var renderDates = function renderDates() {

    content.find('section.event header time, span.btmarket__name time.eventStartTime')
        .each(renderDate);
};

/**
 * Render the date as an element's text based on its datetime attribute
 *
 * @returns {void}
 *
 * @private
 */
var renderDate = function () {

    var startDate = BasePage.jQuery(this).attr('datetime');
    BasePage.jQuery(this).text(BasePage.Date.format(startDate, 'DD MMM HH:mm'));
};

/**
 * Initializing partial view
 *
 * @returns {void}
 *
 * @private
 */
var initPartialView = function initPartialView() {

    var modelBoxCopy = _.extend({}, window.WH.sportsbook.modelBox);

    modelBoxCopy.events = _.filter(
        modelBoxCopy.events,
        existsInModel
    );
    BasePage.setOddsFormat();
    BasePage.ModelComposer.regenerateMergedModel(window.WH.sportsbook.model);
    _.each(
        BasePage.ModelComposer.convertHashModelBox(modelBoxCopy),
        subscribeModel
    );
    SwiperNavigation.initializeAllSwipers();
    Tabs.init();
    renderDates();
};

/**
 * Function binds event handles to various buttons/links on the page
 *
 * @param {Object} content jQuery object containing the page content
 * @param {Object} deps    Page dependencies
 * @param {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;

    MarketMenuAdapter.init();
};

/**
 * Method binds event handlers for scrolling
 *
 * @returns {void}
 *
 * @private
 */
var initScrolling = function initScrolling() {

    $window = BasePage.jQuery(window);
    loadedTabs = [];
    homeContent = BasePage.jQuery('.-home');
    homeScrollAction();
    $window.off(events).on(events, _.debounce(homeScrollAction, 250));
};

/**
 * Unbinding event handlers for scrolling
 *
 * @returns {void}
 *
 * @private
 */
var unbindScrolling = function unbindScrolling() {

    if (loadedTabs.length === _.keys(tabMap).length - 1) {

        $window.off(events);
    }
};

/**
 * Function binds event handlers for switching tabs
 *
 * @returns {void}
 *
 * @private
 */
var bindSwitchTabs = function bindSwitchTabs() {

    homeContent
        .find('.tabs a.tabs__nav-item')
        .on('click', homeTabAction);
};

/**
 * Function binds events for lazy loading
 *
 * @returns {void}
 *
 * @private
 */
var bindLazyLoading = function bindLazyLoading() {

    initScrolling();
    bindSwitchTabs();
};

/**
 * Mapping tab for url param
 *
 * @param   {String}    eventId event id to check sport for
 *
 * @returns {Boolean}   Wheather the given event belongs to a racing sport or not.
 *
 * @private
 */
var isRacingEvent = function isRacingEvent(eventId) {

    if (eventId) {

        return _.includes(
            ['OB_SP43', 'OB_SP42'],
            getEventSportId(eventId)
        );
    }

    return false;
};

/**
 * Gets the sport id for the passed eventId
 *
 * @param {String} eventId The event Id
 *
 * @returns {String} Sport ID
 *
 * @private
 */
var getEventSportId = function getEventSportId(eventId) {

    var event = deps.sportsDataRepository.getModel().events[eventId];

    return event && event.sportId ? event.sportId : '';
};

/**
 * Mapping tab for url param
 *
 * @param   {String} tab The name of a tab
 *
 * @returns {String}
 *
 * @private
 */
var mapTabForUrl = function mapTabForUrl(tab) {

    return tabMap[tab];
};

/**
 * Performing action for scrolling
 *
 * @returns {void}
 *
 * @private
 */
var homeScrollAction = function homeScrollAction() {
    if (DeviceDetector.isDesktop()) {

        homeContent.find('.tabs__panels-panel ')
            .each(checkForPanelReloading);
    }
};

/**
 * Getting the name of tab
 *
 * @param   {Object} panel The panel jQuery element
 *
 * @returns {String}       The name of tab or default highlights
 *
 * @private
 */
var getTabName = function getTabName(panel) {

    var tab = homeContent.find('.tabs__nav')
        .find('[data-link-panel="' + panel.data('panel') + '"]');

    return tab.data('name') || 'Highlights';
};

/**
 * Check if a panel should be reloaded
 *
 * @param {string} tabName The name of the tab
 * @param {Object} panel The panel jQuery element
 *
 * @returns {boolean} Whether we should reload the panel or not
 *
 * @private
 */
var shouldReloadPanel = function shouldReloadPanel(tabName, panel) {
    return loadedTabs.indexOf(tabName) === -1 && isElementVisible(panel) &&
        panel.html() === '';
};

/**
 * Check if a panel should be displayed inside the home
 * fragment for the current device type
 *
 * @param {string} tabName The name of the tab
 *
 * @returns {boolean} Whether we should display the panel or not
 *
 * @private
 */
var shouldDisplayPanel = function shouldDisplayPanel(tabName) {
    return !DeviceDetector.isDesktop() || hiddenPanelsInDesktop.indexOf(tabName) === -1;
};

/**
 * Performing panel reloading
 *
 * @param {Number} index The index of panels collection element
 * @param {Object} panel The panel DOM element
 *
 * @returns {void}
 *
 * @private
 */
var checkForPanelReloading = function checkForPanelReloading(index, panel) {

    panel = BasePage.jQuery(panel);

    var tabName = getTabName(panel);

    if (shouldDisplayPanel(tabName) && shouldReloadPanel(tabName, panel)) {

        reloadPanel(tabName, panel);
    }
};

/**
 * Performing panel reloading
 *
 * @param   {String} tabName The tab name
 * @param   {Object} panel   The panel jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var reloadPanel = function reloadPanel(tabName, panel) {

    loadedTabs.push(tabName);
    loadingCounter++;

    var url = getUrlForFragment(tabName);

    var selector = '[data-panel="' + panel.data('panel') + '"]';

    reloadTabContent(url, selector, tabName);
};

/**
 * Getting the url address
 *
 * @param   {String} tabName The name of a tab
 *
 * @returns {String}
 *
 * @private
 */
var getUrlForFragment = function getUrlForFragment(tabName) {

    if (tabName === 'Top Bets') {

        return URLProvider.homepage.topBetsFragment({
            locale: deps.preferencesProvider.getLocale(),
            tabName: mapTabForUrl(tabName),
            sport: 'homepage',
        });
    }

    return URLProvider.homepage.homeFragment({
        locale: deps.preferencesProvider.getLocale(),
        tabName: mapTabForUrl(tabName),
    });
};

/**
 * Checking if element is visible
 *
 * @param   {Object}  elem The element to check
 *
 * @returns {Boolean}
 *
 * @private
 */
var isElementVisible = function isElementVisible(elem) {

    var docViewTop = $window.scrollTop();
    var docViewBottom = docViewTop + $window.height();

    var elemTop = elem.offset().top;

    return elemTop < docViewBottom;
};

/**
 * Resetting the view
 *
 * @param   {String} tabName The name of tab
 *
 * @returns {void}
 *
 * @private
 */
var resetView = function resetView() {

    loadingCounter--;
    initPartialView();
    unbindScrolling();
    LazyLoadingImage.init();
};

/**
 * Reloading the tab content
 *
 * @param   {String} url           The url of fragment request
 * @param   {String} panelSelector The element to reload selector
 * @param   {String} tabName       The name of a tab
 *
 * @returns {void}
 *
 * @private
 */
var reloadTabContent = function reloadTabContent(url, panelSelector, tabName) {

    deps.pageCommon.reloadSnippet(
        {
            'requestURL': url,
        },
        panelSelector,
        {
            'afterSuccess': resetView.bind(this, tabName),
        },
        true
    );
};

/**
 * Checking if container is empty
 *
 * @param   {String}  selector The container selector
 *
 * @returns {Boolean}
 *
 * @private
 */
var isEmptyContainer = function isEmptyContainer(selector) {

    return homeContent.find(selector).html() === '';
};

/**
 * Performing action for switching tab
 *
 * @returns {void}
 *
 * @private
 */
var homeTabAction = function homeTabAction() {

    var tab = BasePage.jQuery(this);

    var selector = '[data-panel="' + tab.data('link-panel') + '"]';

    var tabName = tab.data('name');

    if (isEmptyContainer(selector) && loadedTabs.indexOf(tabName) === -1) {

        loadedTabs.push(tabName);
        reloadTabContent(getUrlForFragment(tabName), selector, tabName);
    }
};

/**
 * Subscribe a set of entities (events, markets, etc) to PUSH updates (excludes Native Android)
 *
 * @param {Object} set An object containing entities such as markets, selections, events
 *
 * @returns {void}
 *
 * @private
 */
var subscribeSetToPush = function (set, entityType) {
    if (!DeviceDetector.isNativeAndroid()) {
        if (entityType !== 'competitions') {
            _.each(set, subscribeEntityToPush);
        }
    }
};

/**
 * Subscribe a single entity to PUSH updates (excludes Native Android)
 *
 * @param {Object} entity Market/event/selection model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeEntityToPush = function (entity) {
    if (!DeviceDetector.isNativeAndroid()) {
        if (entity && entity.id && deps && deps.pageCommon
            && deps.pageCommon.DomPushAdapter.subscribe(entity, push.callback)) {

            deps.pageCommon.PushAdapter.subscribe(entity.pdsTopic);
        }
    }
};

/**
 * This function checks if the entity was the last one displayed in order to hide containers
 * or to unhide containers if the entity is about to be displayed
 *
 * @param {Object}  $event jQuery element
 * @param {Boolean} hide Hide flag
 *
 * @returns {void}
 *
 * @private
 */
var manageEventContainersHeaders = function ($event, hide) {

    // A hierarchical parent containers selectors array going from event level up
    var eventContainersSelectors = [
        '.events-group-container',
        '.markets-group-container',
        '.sport-container',
    ];

    // on hide === true - check if we need to hide parent containers
    // on hide === false - check if we need to 'unhide' parent containers
    if (hide) {

        hideEventContainers($event, '.event', eventContainersSelectors);
    } else {

        showEventContainers($event, eventContainersSelectors);
    }
};

/**
 * Checks if entity is still not visible - it has to unhide parent containers
 *
 * @param {Object} $event jQuery element
 * @param {Array} eventContainersSelectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var showEventContainers = function ($event, eventContainersSelectors) {

    if (!$event.hasClass('displayNone')) {

        showEntityParentContainers($event, eventContainersSelectors);
    }

};

/**
 * Function propagates event parent containers hide action within given selectors Array
 *
 * @param {Object} $entity jQuery element (current entity in the loop)
 * @param {String} entitySelector jQuery element selector
 * @param {Array} selectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var hideEventContainers = function ($entity, entitySelector, selectors) {

    var parentSelector = null;

    if (selectors.length > 0) {

        parentSelector = selectors.shift();
        hideParentContainer($entity, entitySelector, parentSelector, selectors);
    }
};

/**
 * Function checks hides parent container, if all of the same level elements are already hidden.
 * And calls back hideEventContainers function to process with the rest of selectors
 *
 * @param {Object} $entity jQuery element (current entity in the loop)
 * @param {String} entitySelector jQuery element selector
 * @param {String} parentSelector jQuery element selector
 * @param {Array} selectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var hideParentContainer = function ($entity, entitySelector, parentSelector, selectors) {

    var $parentContainer = $entity.parents(parentSelector);

    // If all Entity siblings are hidden, hide parent container
    if (!hasVisibleSiblings($entity, entitySelector)) {

        $parentContainer.addClass('displayNone');
        hideEventContainers($parentContainer, parentSelector, selectors);
    }
};

/**
 * Function goes recursively through jQuery selectors Array and unhides all parent containers
 *
 * @param {Object} $entity jQuery element
 * @param {Array} selectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var showEntityParentContainers = function ($entity, selectors) {

    var parentSelector, $parentContainer;

    if (selectors.length > 0 && !$entity.hasClass('displayNone')) {

        parentSelector = selectors.shift();
        $parentContainer = $entity.parents(parentSelector);
        $parentContainer.removeClass('displayNone');
        showEntityParentContainers($parentContainer, selectors);
    }
};

/**
 * Checks if the element has at least one visible siblings. Breaks on first match
 *
 * @param {jQuery} $element Element to check
 * @param {String} [selector] Optional selector for the siblings
 *
 * @returns {boolean} true if the element has visible siblings
 *
 * @private
 */
var hasVisibleSiblings = function ($element, selector) {

    var siblings = $element.siblings(selector);

    return _.some(siblings, isVisibleElement);
};

/**
 * Checks if the provided element *does not* have the class 'displayNone'
 *
 * @param element
 *
 * @returns {boolean} true if it has class displayNone
 *
 * @private
 */
var isVisibleElement = function (element) {

    return !element.classList.contains('displayNone');
};

/**
 * Push related functionality
 *
 * @type {Object}
 */
var push = {

    /**
     * DOM PUSH update callback function
     * This function is called every time a push
     * message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: function (message) {

        if (push.handlers.hasOwnProperty(message.attribute)) {
            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {
        'betInRun': 'betInRunUpdateCase',
        'displayed': 'displayedUpdateCase',
        'startDateTime': 'startDateTimeUpdateCase',
    },

    /**
     * Will handle the startDateTime push message for event
     *
     * @param {Object} message Update change message
     *
     * @return {void}
     *
     * @private
     */
    startDateTimeUpdateCase: (message) =>
        LiveScore.startDateTimeUpdateCase(message, content),

    /**
     * Handles the dom push message containing an display attribute
     * The name of this function is linked to push.handlers
     *
     * @param  {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    displayedUpdateCase: function (message) {

        if (message.nodeType === 'events') {

            var $event = BasePage.jQuery('#' + message.id);

            manageEventContainersHeaders($event, !message.value);
            LiveScore.handleLivescore(message, content);
        }
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     * The name of this function is linked to push.handlers
     *
     * @param  {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateCase: function (message) {

        var $event;

        if (message.nodeType === 'events' && message.id && !isRacingEvent(message.id)) {

            BasePage.propagateTopBetsBetInRun(message);

            $event = BasePage.jQuery('#' + message.id);

            $event.addClass('displayNone');

            manageEventContainersHeaders($event, true);
            LiveScore.handleLivescore(message, content);
        }
    },
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Return the URL associated with this page based on the args
     *
     * @param {Object} args Page arguments
     *
     * @returns {String} Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        return '/betting/' + args.locale;
    },

    /**
     * Function called after the DOM ready event
     * is fired
     *
     * @param   {Object} deps Object that contains page
     *                        dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(BasePage.jQuery, 'home', args.sport);

        if (deps.ready) {
            deps.ready('home');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @param   {Object} deps    Object that contains page dependencies
     * @param   {Object} content jQuery object that contains this page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function (deps, content) {

        Tabs.init();
        SwiperNavigation.initializeAllSwipers();
        initViews();
        // It's fake tab name, so we can switch back to home content, because this method is in fact
        // setting the class for the main container in tab switching way...
        BasePage.switchTab('Home');
        FirstTimeUserJourney.init(FirstTimeUserJourney.getPages().HOME);
        bindLazyLoading();

        LiveScore.setCountDownTimer(content);
        HiddenMarketsWidget.start();
    },

    bindNavigation: bindNavigation,

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {
        rendered = true;
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: function () {
        HiddenMarketsWidget.stop();

        content = null;
        deps = null;
        rendered = false;
    },

    /**
     * Returns the value of the internal rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {
        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'home',
};

export default api;